import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { useGetSet } from 'react-use';
import { cloneDeep, get, has, isEmpty, isEqual, isFunction, keys, noop, set, unset, values } from 'lodash';
import PropTypes from 'prop-types';
import { useImmer } from 'use-immer';
import { FORM_MODES } from 'config/formModes';
import { useCurrentValueGetter } from 'hooks/useCurrentValueGetter';
import useShallowObject from 'hooks/useShallowObject';
import { AccountContext } from '.././account-configurations/components/AccountContext';
import { APP_EVENT_TYPE, useAppEventContext } from '../app-events-aggregator';
import { useHasUnsavedChanges } from './hooks/useHasUnsavedChanges';
import { getParentArrayField, isArrayElementField } from './utils/formContextUtils';

const getNewValue = (data, field, value) => {
    const currentValue = get(data, field);
    const newValue = isFunction(value) ? value(currentValue) : value;

    return newValue;
};

export const FormDataContext = createContext({
    data: Object.freeze({}),
    setData: noop,
    setFetchedData: noop,
    setField: noop,
    initialData: Object.freeze({}),
    touchedFormFields: Object.freeze({}),
    mutatedFormFields: Object.freeze({}),
    isFormTouched: false,
    isCurrentlyDirty: () => false,
    registerField: noop,
    isFieldEqualToInitial: noop,
    setDelayedFieldState: noop,
    waitDelayedFields: noop,
    defaultValuesCache: {},
    isExternalData: noop,
});

const unsetWithChildren = (obj, field) => {
    keys(obj)
        .filter(key => key === field || key.startsWith(`${field}.`))
        .forEach(key => {
            unset(obj, key);
        });
};

const useDelayedFields = () => {
    const delayedValuesRef = useRef({});
    const setDelayedFieldState = useCallback((field, synced) => {
        if (delayedValuesRef.current[field]?.synced === synced || (!delayedValuesRef.current[field] && synced)) {
            return;
        }

        if (!synced) {
            let resolver;
            const promise = new Promise(resolve => (resolver = resolve));

            delayedValuesRef.current[field] = {
                synced: false,
                promise,
                resolver,
            };
            return;
        }

        delayedValuesRef.current[field].synced = true;
        delayedValuesRef.current[field].resolver();
        unset(delayedValuesRef.current[field], 'promise');
        unset(delayedValuesRef.current[field], 'resolver');
    }, []);
    const unsetDelayedFieldState = useCallback(field => unset(delayedValuesRef.current, field), []);
    const waitDelayedFields = useCallback(() => {
        const promises = values(delayedValuesRef.current)
            .filter(({ synced }) => !synced)
            .map(({ promise }) => promise);

        return Promise.all(promises);
    }, []);

    return {
        setDelayedFieldState,
        unsetDelayedFieldState,
        waitDelayedFields,
    };
};

export const FormDataProvider = ({
    children,
    initialData = {},
    isSubmitDisabled,
    mode,
    formContainerId: _formContainerId,
    enableChangePreviewModeData,
    ...rest
}) => {
    const defaultValuesCache = useRef({ renderCounter: 0 });
    ++defaultValuesCache.current.renderCounter;
    const [data, setData] = useImmer(initialData);
    const [response, setResponse] = useState();
    const dataGetter = useCurrentValueGetter(data);
    const memoizedInitialData = useMemo(() => cloneDeep(initialData), [initialData]);
    const { setDelayedFieldState, unsetDelayedFieldState, waitDelayedFields } = useDelayedFields();
    const initialDataRef = useRef(memoizedInitialData);
    const fetchedDataRef = useRef(memoizedInitialData);
    const touchedFormFields = useRef({});
    const registeredFields = useRef({});
    const externalData = useRef({});
    const setExternalData = useCallback(passedData => {
        externalData.current = passedData;
    }, []);
    const mutatedFormFields = useRef({});
    const isFormTouched = !isEmpty(touchedFormFields.current);
    const [isInitialRecommendationsReady, setIsInitialRecommendationsReady] = useState(false);

    // TODO remove this bridge
    const { account, setAccountId, isAccountTouched, setAccountTouched } = useContext(AccountContext);
    const formAccountProps = {
        formAccount: account,
        setFormAccount: account => setAccountId(account.id),
        isFormAccountTouched: isAccountTouched,
    };

    const memoRest = useShallowObject({ ...formAccountProps, ...rest });

    const setFetchedData = useCallback(fetchedData => {
        fetchedDataRef.current = fetchedData;
        initialDataRef.current = cloneDeep(fetchedData);
    }, []);

    const updateMutatedFields = useCallback(
        (field, value) => {
            const newValue = getNewValue(dataGetter(), field, value);

            if (isEqual(get(initialDataRef.current, field), newValue)) {
                unsetWithChildren(mutatedFormFields.current, field);
                return;
            }

            mutatedFormFields.current[field] = true;
        },
        [dataGetter]
    );

    const isFieldEqualToInitial = useCallback(field => get(mutatedFormFields.current, field) !== true, []);

    const unregisterField = useCallback(field => unsetDelayedFieldState(field), [unsetDelayedFieldState]);
    const registerField = useCallback(
        field => {
            registeredFields.current[field] = field;
            if (has(externalData.current, field)) {
                touchedFormFields.current[field] = true;
                mutatedFormFields.current[field] = true;
            }

            return () => {
                unsetWithChildren(registeredFields.current, field);
                unregisterField(field);
            };
        },
        [unregisterField]
    );
    const isExternalData = useCallback(
        field =>
            has(externalData.current, field) && isEqual(get(externalData.current, field), get(dataGetter(), field)),
        [dataGetter]
    );
    const isCurrentlyDirty = useCallback(field => get(touchedFormFields.current, field) === true, []);

    const isSelfOrParentDirty = useCallback(
        field => isCurrentlyDirty(field) || isCurrentlyDirty(getParentArrayField(field)),
        [isCurrentlyDirty]
    );
    const { push: pushAppEvent } = useAppEventContext();

    const setTouchedFormFields = useCallback(
        ({ field, value }) => {
            touchedFormFields.current[field] = true;
            updateMutatedFields(field, value);
            if (isArrayElementField(field)) {
                const parentField = getParentArrayField(field);
                touchedFormFields.current[parentField] = true;
            }
        },
        [updateMutatedFields]
    );

    const [isUnmounted, setUnmounted] = useGetSet(false);
    const isEditable = useCallback(
        () => (mode !== FORM_MODES.PREVIEW || enableChangePreviewModeData) && !isUnmounted(),
        [mode, isUnmounted, enableChangePreviewModeData]
    );
    const setField = useCallback(
        ({ field, value, isInitial = false }) => {
            if (!isEditable()) {
                return;
            }

            if (isInitial) {
                set(initialDataRef.current, field, value);
            } else {
                setTouchedFormFields({ field, value });
            }

            setData(draft => {
                const currentValue = get(draft, field);
                const valueToSet = getNewValue(draft, field, value);

                if (isEqual(valueToSet, currentValue)) {
                    return draft;
                }

                pushAppEvent({
                    type: APP_EVENT_TYPE.FORM_FIELD_CHANGE,
                    id: field,
                    value: valueToSet,
                    prevValue:
                        typeof currentValue === 'object' && currentValue !== null
                            ? cloneDeep(currentValue)
                            : currentValue,
                });

                return set(draft, field, valueToSet);
            });
        },
        [isEditable, setData, setTouchedFormFields, pushAppEvent]
    );

    const hasDataChanges = useHasUnsavedChanges(mutatedFormFields.current);
    const beforeUnmount = useCallback(() => {
        touchedFormFields.current = {};
        mutatedFormFields.current = {};
        batch(() => {
            setAccountTouched(false);
            setUnmounted(true);
        });
    }, [setAccountTouched, setUnmounted]);

    const resetFormToInitial = useCallback(() => {
        touchedFormFields.current = {};
        mutatedFormFields.current = {};

        setData(cloneDeep(initialDataRef.current));
    }, [setData]);

    const resetFieldToInitial = useCallback(
        field => {
            const initialValue = get(initialDataRef.current, field);
            unsetWithChildren(touchedFormFields.current, field);
            unsetWithChildren(mutatedFormFields.current, field);
            setData(draft => set(draft, field, initialValue));
            return initialValue;
        },
        [setData]
    );

    const patchExternalData = useCallback(
        ({ data, merger }) => {
            if (!isEditable()) {
                return;
            }

            keys(touchedFormFields.current).forEach(key => {
                if (has(data, key)) {
                    resetFieldToInitial(key);
                }
            });

            externalData.current = merger(cloneDeep(externalData.current), data);
            setData(draft => merger(draft, data));

            keys(registeredFields.current).forEach(key => {
                if (has(externalData.current, key)) {
                    touchedFormFields.current[key] = true;
                    mutatedFormFields.current[key] = true;
                }
            });
        },
        [isEditable, resetFieldToInitial, setData]
    );

    const hasNoEditChanges = !hasDataChanges && mode === FORM_MODES.EDIT;

    const value = useMemo(
        () => ({
            defaultValuesCache: defaultValuesCache.current,
            data,
            setData,
            patchExternalData,
            setFetchedData,
            setField,
            initialData: initialDataRef.current,
            mutatedFormFields: mutatedFormFields.current,
            fetchedData: fetchedDataRef.current,
            resetFormToInitial,
            touchedFormFields: touchedFormFields.current,
            isFormTouched,
            isCurrentlyDirty,
            isSelfOrParentDirty,
            resetFieldToInitial,
            hasDataChanges,
            isFieldEqualToInitial,
            hasNoEditChanges,
            isSubmitDisabled: isSubmitDisabled || hasNoEditChanges,
            externalData: externalData.current,
            setExternalData,
            registerField,
            mode,
            setDelayedFieldState,
            waitDelayedFields,
            isExternalData,
            setTouchedFormFields,
            response,
            setResponse,
            beforeUnmount,
            enableChangePreviewModeData,
            isInitialRecommendationsReady,
            setIsInitialRecommendationsReady,
            ...memoRest,
        }),
        [
            data,
            setData,
            setFetchedData,
            patchExternalData,
            setField,
            resetFormToInitial,
            isFormTouched,
            isCurrentlyDirty,
            isSelfOrParentDirty,
            resetFieldToInitial,
            hasDataChanges,
            isFieldEqualToInitial,
            mode,
            isSubmitDisabled,
            hasNoEditChanges,
            memoRest,
            setExternalData,
            registerField,
            setDelayedFieldState,
            waitDelayedFields,
            isExternalData,
            setTouchedFormFields,
            response,
            beforeUnmount,
            enableChangePreviewModeData,
            isInitialRecommendationsReady,
            setIsInitialRecommendationsReady,
        ]
    );

    return <FormDataContext.Provider value={value}>{children}</FormDataContext.Provider>;
};

FormDataProvider.propTypes = { children: PropTypes.node };

export const useFormDataContext = () => useContext(FormDataContext);
